import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    CircularProgress,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import { validateBusinessToken, sendSMSCode, verifySMSLogin } from './services/phoneLoginService';
import { usePermissions } from '../hooks/usePermissions';

const PhoneLoginPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const { refreshPermissions, isAuthenticated, isLoading: authLoading } = usePermissions();
    const { token } = useParams();
    const [step, setStep] = useState(1); // 1 = phone input, 2 = code verification
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [businessName, setBusinessName] = useState('');

    // Redirect to projects if already authenticated
    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/projects');
        }
    }, [authLoading, isAuthenticated, navigate]);

    useEffect(() => {
        const initializeSessionAndValidateToken = async () => {
            const API_URL = process.env.REACT_APP_API_URL;
            try {
                await fetch(`${API_URL}/welcome`, {
                    method: 'GET',
                    credentials: 'include',
                });

                if (token) {
                    await handleValidateBusinessToken();
                }
            } catch (error) {
                console.error('Error initializing session:', error);
            }
        };

        initializeSessionAndValidateToken();
    }, [token]);

    const handleValidateBusinessToken = async () => {
        try {
            const data = await validateBusinessToken(token);
            if (data && data.businessName) {
                setBusinessName(data.businessName);
            }
        } catch (error) {
            console.log('Token validation failed, proceeding without business name');
        }
    };

    // Countdown timer for 60-second cooldown
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleSendCode = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        try {
            await sendSMSCode(phoneNumber);
            setStep(2);
            setCountdown(60);
        } catch (error) {
            if (error.response?.status === 401) {
                setErrorMessage('Session expired. Please refresh the page and try again.');
            } else {
                setErrorMessage(error.response?.data?.error || 'Failed to send verification code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        try {
            await verifySMSLogin(phoneNumber, verificationCode);
            await refreshPermissions();
            navigate('/projects');
        } catch (error) {
            if (error.response?.status === 401) {
                setErrorMessage('Session expired. Please refresh the page and try again.');
            } else {
                setErrorMessage(error.response?.data?.error || 'Authentication failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhoneNumberChange = (e) => {
        const value = e.target.value;
        const phoneRegex = /^\d*$/;
        if (phoneRegex.test(value)) {
            setPhoneNumber(value);
        }
    };

    const handleVerificationCodeChange = (e) => {
        const value = e.target.value;
        const codeRegex = /^\d*$/;
        if (codeRegex.test(value)) {
            setVerificationCode(value);
        }
    };

    const handleBackToPhoneInput = () => {
        setStep(1);
        setVerificationCode('');
        setErrorMessage('');
    };

    const inputSx = {
        backgroundColor: '#FFFFFF',
        borderRadius: '10px',
        boxShadow: 5,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(0,0,0,0.25)', borderWidth: '1px' },
        },
        input: { fontSize: '16px' },
    };

    return (
        <Grid container sx={{ minHeight: '100vh', width: '100%' }}>
            {/* Left panel - Gradient background with logo */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    background: `linear-gradient(rgba(11, 21, 55, 0.75), rgba(11, 21, 55, 0.75)), url('/Moonlight-Connect-login-background.svg') #0B1537`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: isMobile ? '200px' : '100vh',
                    p: 4,
                }}
            >
                <Box
                    component="img"
                    sx={{
                        width: isMobile ? '280px' : isTablet ? '500px' : '750px',
                    }}
                    src="/MOONLIGHT-quality-logo.svg"
                    alt="Moonlight Quality"
                />
            </Grid>

            {/* Right panel - Phone login form */}
            <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, sm: 4, md: 6 },
                    background: '#F3F3F3',
                }}
            >
                {businessName && (
                    <Box sx={{ textAlign: 'center', mb: 2, width: '100%', maxWidth: '400px' }}>
                        <Typography variant="body2" color="text.secondary">
                            Login for
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0B1537' }}>
                            {businessName}
                        </Typography>
                    </Box>
                )}

                {step === 1 ? (
                    <>
                        <Box
                            sx={{
                                textAlign: 'left',
                                width: '100%',
                                maxWidth: '400px',
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    mb: 1,
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                }}
                            >
                                PHONE LOGIN
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: '#545454' }}>
                                Enter your phone number to receive a verification code
                            </Typography>
                        </Box>

                        <Box
                            component="form"
                            onSubmit={handleSendCode}
                            noValidate
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                maxWidth: '400px',
                            }}
                        >
                            <TextField
                                required
                                fullWidth
                                id="phoneNumber"
                                label="Phone Number"
                                name="phoneNumber"
                                placeholder="1234567890"
                                autoFocus
                                value={phoneNumber}
                                onChange={handlePhoneNumberChange}
                                margin="normal"
                                sx={inputSx}
                            />

                            {errorMessage && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 1, textAlign: 'center' }}
                                >
                                    {errorMessage}
                                </Typography>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    background: '#FBBB1D',
                                    height: '56px',
                                    mt: 3,
                                    mb: 2,
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 700,
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                    borderRadius: '10px',
                                    boxShadow: 5,
                                    color: '#fff',
                                    '&:hover': {
                                        background: '#E5AA15',
                                    },
                                    '&.Mui-disabled': {
                                        background: '#FBBB1D',
                                        opacity: 0.7,
                                        color: '#fff',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={28} sx={{ color: '#fff' }} />
                                ) : (
                                    'SEND CODE'
                                )}
                            </Button>

                            <Box sx={{ textAlign: 'left', width: '100%' }}>
                                <Link
                                    href="/"
                                    underline="hover"
                                    sx={{
                                        color: '#545454',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Back to Email Login
                                </Link>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                textAlign: 'left',
                                width: '100%',
                                maxWidth: '400px',
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    mb: 1,
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                }}
                            >
                                VERIFY CODE
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: '#545454' }}>
                                Enter the 6-digit code sent to {phoneNumber}
                            </Typography>
                        </Box>

                        <Box
                            component="form"
                            onSubmit={handleVerifyCode}
                            noValidate
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                maxWidth: '400px',
                            }}
                        >
                            <TextField
                                required
                                fullWidth
                                id="verificationCode"
                                label="Verification Code"
                                name="verificationCode"
                                placeholder="123456"
                                autoFocus
                                value={verificationCode}
                                onChange={handleVerificationCodeChange}
                                margin="normal"
                                slotProps={{ htmlInput: { maxLength: 6 } }}
                                sx={inputSx}
                            />

                            {errorMessage && (
                                <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ mt: 1, textAlign: 'center' }}
                                >
                                    {errorMessage}
                                </Typography>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    background: '#FBBB1D',
                                    height: '56px',
                                    mt: 3,
                                    mb: 1,
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 700,
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                    borderRadius: '10px',
                                    boxShadow: 5,
                                    color: '#fff',
                                    '&:hover': {
                                        background: '#E5AA15',
                                    },
                                    '&.Mui-disabled': {
                                        background: '#FBBB1D',
                                        opacity: 0.7,
                                        color: '#fff',
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={28} sx={{ color: '#fff' }} />
                                ) : (
                                    'VERIFY & LOGIN'
                                )}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleSendCode}
                                disabled={countdown > 0 || isLoading}
                                sx={{
                                    mt: 1,
                                    mb: 2,
                                    height: '48px',
                                    borderRadius: '10px',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                }}
                            >
                                {countdown > 0 ? `Resend Code (${countdown}s)` : 'Resend Code'}
                            </Button>

                            <Box sx={{ textAlign: 'left', width: '100%' }}>
                                <Link
                                    onClick={handleBackToPhoneInput}
                                    underline="hover"
                                    sx={{
                                        color: '#545454',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Change Phone Number
                                </Link>
                            </Box>
                        </Box>
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default PhoneLoginPage;
